// src/components/layout/DragDropZone.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useDragDrop } from '@/managers/DragDropManager';
import { useFileAttachment } from '@/managers/FileAttachmentManager';
import { PanelTypes, usePanelManager } from '@/managers/PanelManager';
import { workspaceManager } from '@/managers/workspaceManager';
import { authManager } from '@/managers/AuthManager';
import { apiManager } from '@/components/layout/RightPanel/shelbulaworkspaces/apiManager';
import { canAccessWorkspaces } from '@/utils/workspaceAccess';
import { isWorkspaceBackendEnabled } from '@/utils/setProdOrDev';

const MOBILE_BREAKPOINT = 768;
const SHARED_RIGHT_SIDEBAR_DEFAULT_WIDTH = 65;

const TEXT_FILE_EXTENSIONS = new Set([
  'c', 'cc', 'cpp', 'cs', 'css', 'csv', 'dart', 'env', 'gitignore', 'go', 'graphql', 'h', 'hpp',
  'html', 'java', 'js', 'json', 'jsx', 'kt', 'kts', 'log', 'md', 'mjs', 'php', 'pl', 'py',
  'rb', 'rs', 'scss', 'sh', 'sql', 'svg', 'swift', 'toml', 'ts', 'tsx', 'txt', 'vue', 'xml',
  'yaml', 'yml', 'zsh'
]);

const ZIP_MIME_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'multipart/x-zip'
]);

const isZipFile = (file) => {
  const fileName = String(file?.name || '').toLowerCase();
  return fileName.endsWith('.zip') || ZIP_MIME_TYPES.has(file?.type || '');
};

const getFileExtension = (fileName = '') => {
  const cleanName = String(fileName).trim().toLowerCase();
  if (!cleanName) return '';
  const lastDotIndex = cleanName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === cleanName.length - 1) {
    return cleanName;
  }
  return cleanName.slice(lastDotIndex + 1);
};

const isLikelyWorkspaceTextFile = (file) => {
  const mimeType = String(file?.type || '').toLowerCase();
  const extension = getFileExtension(file?.name || '');

  if (
    mimeType.startsWith('text/')
    || mimeType.includes('json')
    || mimeType.includes('javascript')
    || mimeType.includes('typescript')
    || mimeType.includes('xml')
    || mimeType.includes('yaml')
    || mimeType.includes('toml')
    || mimeType.includes('shellscript')
  ) {
    return true;
  }

  return TEXT_FILE_EXTENSIONS.has(extension);
};

const readFileAsText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    resolve({
      filePath: file.name,
      fileContent: typeof event.target?.result === 'string' ? event.target.result : '',
    });
  };
  reader.onerror = () => reject(new Error(`Failed to read ${file.name}.`));
  reader.readAsText(file);
});

const readEntryAsText = ({ file, relativePath }) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    resolve({
      filePath: relativePath,
      fileContent: typeof event.target?.result === 'string' ? event.target.result : '',
    });
  };
  reader.onerror = () => reject(new Error(`Failed to read ${relativePath}.`));
  reader.readAsText(file);
});

const readAllDirEntries = (reader) => new Promise((resolve, reject) => {
  const all = [];
  const readNext = () => reader.readEntries((batch) => {
    if (!batch.length) { resolve(all); return; }
    all.push(...batch);
    readNext();
  }, reject);
  readNext();
});

const collectFilesFromEntry = async (entry, prefix = '') => {
  if (entry.isFile) {
    return [await new Promise((resolve, reject) =>
      entry.file((file) => resolve({ file, relativePath: prefix + file.name }), reject)
    )];
  }
  if (entry.isDirectory) {
    const entries = await readAllDirEntries(entry.createReader());
    const nested = await Promise.all(entries.map((e) => collectFilesFromEntry(e, prefix + entry.name + '/')));
    return nested.flat();
  }
  return [];
};

const extractSkillName = (content) => {
  const match = String(content || '').match(/^#\s+(.+)$/m);
  if (!match) return null;
  return match[1].trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
};

const DragDropZone = () => {
  const { addFiles, isLoading } = useFileAttachment();
  const { panelWidths } = usePanelManager();

  const {
    dragCounter,
    modalOpen,
    pinnedConstantsOpen,
    incrementDragCounter,
    decrementDragCounter,
    resetDragCounter,
    activeDropZone,
  } = useDragDrop();

  const [pointerX, setPointerX] = useState(null);
  const [isProcessingDrop, setIsProcessingDrop] = useState(false);

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const canUseWorkspaceSplit = canAccessWorkspaces(authManager.getState().user, isWorkspaceBackendEnabled())
    && viewportWidth >= MOBILE_BREAKPOINT;

  const rightPanelWidthPercent = useMemo(() => {
    return panelWidths?.[PanelTypes.CODER] ?? SHARED_RIGHT_SIDEBAR_DEFAULT_WIDTH;
  }, [panelWidths]);

  const leftPanelWidthPercent = useMemo(() => {
    if (!canUseWorkspaceSplit) return 100;
    return Math.max(0, 100 - rightPanelWidthPercent);
  }, [canUseWorkspaceSplit, rightPanelWidthPercent]);

  const splitBoundaryX = useMemo(() => {
    return viewportWidth * (leftPanelWidthPercent / 100);
  }, [leftPanelWidthPercent, viewportWidth]);

  const resolveDropTarget = useCallback((clientX) => {
    if (!canUseWorkspaceSplit) return 'chat';
    return clientX >= splitBoundaryX ? 'workspace' : 'chat';
  }, [canUseWorkspaceSplit, splitBoundaryX]);

  const activeDropTarget = useMemo(() => {
    if (pointerX === null) return 'chat';
    return resolveDropTarget(pointerX);
  }, [pointerX, resolveDropTarget]);

  const handleChatDrop = useCallback(async (files) => {
    if (!files.length) return;
    await addFiles(files);
  }, [addFiles]);

  const handleWorkspaceDrop = useCallback(async (entries) => {
    if (!entries.length) return;

    const workspaceId = workspaceManager.getState().activeWorkspaceId;

    const zipEntries = entries.filter(({ file }) => isZipFile(file));
    const standardEntries = entries.filter(({ file }) => !isZipFile(file));
    const textEntries = standardEntries.filter(({ file, relativePath }) =>
      isLikelyWorkspaceTextFile({ type: file.type, name: relativePath })
    );
    const unsupportedEntries = standardEntries.filter(({ file, relativePath }) =>
      !isLikelyWorkspaceTextFile({ type: file.type, name: relativePath })
    );

    if (zipEntries.length === 0 && textEntries.length === 0) {
      toast.error('Binary files like images or PDFs go on the chat side. Workspace accepts code, text, and .zip files.');
      return;
    }

    const count = zipEntries.length + textEntries.length;
    const toastId = toast.loading(`Adding ${count} file${count > 1 ? 's' : ''} to workspace\u2026`);

    try {
      for (const { file } of zipEntries) {
        await apiManager.uploadAndUnzip(workspaceId, file);
      }

      if (textEntries.length > 0) {
        const filesToUpload = await Promise.all(textEntries.map(async (entry) => {
          const result = await readEntryAsText(entry);
          // Auto-place a bare SKILL.md drop into the brain skills folder
          const basename = entry.relativePath.split('/').pop();
          if (entry.relativePath === basename && basename.toUpperCase() === 'SKILL.MD') {
            const skillName = extractSkillName(result.fileContent) || `skill-${Date.now()}`;
            return { ...result, filePath: `.WorkspaceBrain/skills/${skillName}/SKILL.md` };
          }
          return result;
        }));
        await apiManager.saveMultipleFiles(workspaceId, filesToUpload);
      }

      await workspaceManager.refreshNow?.();
      window.dispatchEvent(new CustomEvent('workspace-refresh-required'));

      const parts = [];
      if (textEntries.length > 0) parts.push(`${textEntries.length} file${textEntries.length > 1 ? 's' : ''}`);
      if (zipEntries.length > 0) parts.push(`${zipEntries.length} archive${zipEntries.length > 1 ? 's' : ''}`);
      const suffix = unsupportedEntries.length > 0 ? ` (${unsupportedEntries.length} binary skipped)` : '';
      toast.success(`Added ${parts.join(' + ')} to workspace${suffix}`, { id: toastId });
    } catch (error) {
      console.error('Workspace file drop failed:', error);
      toast.error(error?.message || 'Failed to add files to workspace.', { id: toastId });
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading || modalOpen || pinnedConstantsOpen) return;

    const dropTarget = resolveDropTarget(e.clientX);

    try {
      setIsProcessingDrop(true);

      if (dropTarget === 'workspace') {
        // Use DataTransferItemList so folders are traversed recursively
        const dtItems = Array.from(e.dataTransfer.items || []);
        const fsEntries = dtItems
          .filter((i) => i.kind === 'file')
          .map((i) => i.webkitGetAsEntry?.())
          .filter(Boolean);

        let fileEntries;
        if (fsEntries.length > 0) {
          const collected = await Promise.all(fsEntries.map((entry) => collectFilesFromEntry(entry)));
          fileEntries = collected.flat();
        } else {
          fileEntries = Array.from(e.dataTransfer.files || []).map((file) => ({ file, relativePath: file.name }));
        }
        await handleWorkspaceDrop(fileEntries);
      } else {
        const files = Array.from(e.dataTransfer.files || []);
        await handleChatDrop(files);
      }
    } finally {
      setIsProcessingDrop(false);
      setPointerX(null);
      resetDragCounter();
    }
  }, [handleChatDrop, handleWorkspaceDrop, isLoading, modalOpen, pinnedConstantsOpen, resetDragCounter, resolveDropTarget]);

  useEffect(() => {
    if (dragCounter === 0) setPointerX(null);
  }, [dragCounter]);

  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      if (e.dataTransfer.types.includes('Files')) {
        setPointerX(e.clientX);
        incrementDragCounter();
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      decrementDragCounter();
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.types.includes('Files')) {
        setPointerX(e.clientX);
      }
    };

    const handleWindowDrop = (e) => {
      e.preventDefault();
      if (!modalOpen && !pinnedConstantsOpen && !activeDropZone) {
        handleDrop(e);
      } else {
        setPointerX(null);
        resetDragCounter();
      }
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, [activeDropZone, decrementDragCounter, handleDrop, incrementDragCounter, modalOpen, pinnedConstantsOpen, resetDragCounter]);

  const shouldShow = dragCounter > 0 && !modalOpen && !pinnedConstantsOpen && !activeDropZone;
  if (!shouldShow) return null;

  const chatActive = activeDropTarget === 'chat';
  const wsActive = canUseWorkspaceSplit && activeDropTarget === 'workspace';

  return (
    <AnimatePresence>
      <motion.div
        key="drop-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className="fixed inset-0 z-50 select-none"
      >
        {/* Scrim */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[3px]" />

        {/* Split container */}
        <div className="relative flex h-full w-full">

          {/* ── Left zone: Chat ── */}
          <motion.div
            animate={{
              backgroundColor: chatActive
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.015)',
            }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative h-full"
            style={{ width: `${leftPanelWidthPercent}%` }}
          >
            {/* Inset border hint */}
            <motion.div
              animate={{
                borderColor: chatActive
                  ? 'rgba(var(--on-surface-rgb), 0.14)'
                  : 'rgba(var(--on-surface-rgb), 0.04)',
              }}
              transition={{ duration: 0.2 }}
              className="absolute inset-3 rounded-2xl border pointer-events-none"
            />

            {/* Label — vertically centered */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.div
                animate={{ opacity: chatActive ? 1 : 0.35 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col items-center gap-2"
              >
                <span
                  className="text-[13px] font-medium tracking-wide text-on-surface"
                  style={{ letterSpacing: '0.04em' }}
                >
                  Chat
                </span>
                <span className="text-[11px] text-on-surface-dim/70">
                  Attach as context
                </span>
              </motion.div>
            </div>
          </motion.div>

          {/* ── Divider + Right zone: Workspace ── */}
          {canUseWorkspaceSplit && (
            <>
              {/* Divider line */}
              <div
                className="absolute inset-y-0 w-px"
                style={{
                  left: `${leftPanelWidthPercent}%`,
                  background: wsActive
                    ? 'rgba(var(--on-surface-rgb), 0.12)'
                    : chatActive
                      ? 'rgba(var(--on-surface-rgb), 0.12)'
                      : 'rgba(var(--on-surface-rgb), 0.06)',
                  transition: 'background 0.2s ease',
                }}
              />

              {/* Right zone: Workspace */}
              <motion.div
                animate={{
                  backgroundColor: wsActive
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(255,255,255,0.015)',
                }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative h-full"
                style={{ width: `${rightPanelWidthPercent}%` }}
              >
                {/* Inset border hint */}
                <motion.div
                  animate={{
                    borderColor: wsActive
                      ? 'rgba(var(--on-surface-rgb), 0.14)'
                      : 'rgba(var(--on-surface-rgb), 0.04)',
                  }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-3 rounded-2xl border pointer-events-none"
                />

                {/* Label */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <motion.div
                    animate={{ opacity: wsActive ? 1 : 0.35 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col items-center gap-2"
                  >
                    <span
                      className="text-[13px] font-medium tracking-wide text-on-surface"
                      style={{ letterSpacing: '0.04em' }}
                    >
                      Workspace
                    </span>
                    <span className="text-[11px] text-on-surface-dim/70">
                      Add to project files
                    </span>
                  </motion.div>
                </div>
              </motion.div>
            </>
          )}
        </div>

        {/* Processing spinner */}
        {(isLoading || isProcessingDrop) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="rounded-full bg-surface/90 p-3 shadow-2xl backdrop-blur-md">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-on-surface/60 border-t-transparent" />
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default DragDropZone;